import type { CategoryInsights } from "@/api/categoryService";
import { Link } from "@/components/Link";
import { formatRating, toArabicNumerals } from "@/lib/arabicNumerals";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";

// Stores with the most feed-eligible reviews in this category, as a compact ranked strip above
// the experiences feed: the page's store surface now that the grid lives on /stores. Platform
// neutrality: this is a COUNT, labelled as such on the page, never a quality ranking («الأفضل»).
// The rank numerals are justified because the list really is ordered. A tile opens the store; the
// trailing link opens /stores with this category pre-selected (Ali, 2026-10-10).
export default function CategoryMostReviewed({
  insights,
  categoryTitle,
  categorySlug,
  storesHref,
}: {
  insights: CategoryInsights | null;
  categoryTitle: string;
  categorySlug: string;
  storesHref: string;
}) {
  const { trackNavigation } = usePostHogTracking();
  const stores = insights?.mostReviewedStores ?? [];
  if (stores.length === 0) return null;
  const storeCount = insights?.storeCount ?? 0;

  return (
    <section className="catpage-sec" aria-labelledby="category-most-reviewed">
      <div className="catpage-sechead">
        <div>
          <h2 id="category-most-reviewed">{`الأكثر تقييمًا في ${categoryTitle}`}</h2>
          <p>مرتبة حسب عدد التقييمات على يتفرج، وليس حسب الجودة.</p>
        </div>
        <Link
          to={storesHref}
          onClick={() => trackNavigation.categoryInsightClicked(categorySlug, "all_stores", categorySlug)}
          className="catpage-link"
        >
          {storeCount > 0 ? `كل متاجر الفئة (${toArabicNumerals(storeCount)})` : "كل متاجر الفئة"}
        </Link>
      </div>
      <ol className="catpage-strip">
        {stores.map((store, i) => {
          const storeKey = store.slug || store._id;
          return (
            <li key={store._id}>
              <Link
                to={`/store/${storeKey}`}
                onClick={() =>
                  trackNavigation.categoryInsightClicked(categorySlug, "most_reviewed", storeKey)
                }
                className="catpage-tile"
              >
                <div className="catpage-tile-top">
                  <span className="catpage-tile-rank" aria-hidden="true">
                    {toArabicNumerals(i + 1)}
                  </span>
                  {store.logoUrl ? (
                    <img src={store.logoUrl} alt="" loading="lazy" className="catpage-logo" />
                  ) : (
                    <div className="catpage-logo" aria-hidden="true" />
                  )}
                </div>
                <span className="catpage-tile-name">{store.name}</span>
                <span className="catpage-tile-meta">
                  {`${toArabicNumerals(store.reviewCount)} تقييم، متوسط ${formatRating(store.avgRating)}`}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
